import { useSession } from "next-auth/react";
import { MouseEventHandler } from "react";
import { MCPermissions } from "~/common/constants";
import { useAnalytics } from "~/components/Analytics/useAnalytics";
import { PianoSDKStatus, usePiano } from "~/components/Piano/PianoContext";
import { getReportTemplateIds } from "~/lib/client/getPianoTemplateIds";
import { useFetchSignedAssetUrl } from "~/lib/client/useFetchSignedAssetUrl";
import { Button } from "~/ui-library";

export interface ReportPreviewDownloadButtonProps {
  postId: string | number;
  attachmentId: string | number;
  fullWidth?: boolean;
  dataAccess?: MCPermissions[];
}

export function ReportPreviewDownloadButton({
  postId,
  attachmentId,
  fullWidth,
  dataAccess = [],
}: ReportPreviewDownloadButtonProps): JSX.Element {
  const { status } = useSession();
  const { analyticsEnhancedTrack } = useAnalytics();
  const { isMutating, trigger } = useFetchSignedAssetUrl({
    postId,
    attachmentId,
    shouldFetch: true,
  });
  const piano = usePiano();
  const downloadReport: MouseEventHandler = async (e) => {
    e.preventDefault();

    const showPianoModal = () => {
      piano?.piano?.template.show({
        ...getReportTemplateIds(piano.reportTemplates, dataAccess),
        displayMode: "modal",
        showCloseButton: true,
      });
    };

    if (piano && piano.pianoStatus === PianoSDKStatus.Ready) {
      const response = await trigger();
      const isUnauthorized = [401, 403].includes(response?.status!);
      if (!response?.data?.url || isUnauthorized) {
        showPianoModal();
        return;
      }
      const link = document.createElement("a");
      link.href = response.data.url;
      link.download = "";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
      analyticsEnhancedTrack("Report Download");
    }
  };

  return (
    <Button
      fullWidth={fullWidth}
      size="large"
      variant="primary"
      onClick={downloadReport}
      disabled={status === "loading" || isMutating}
    >
      Download the full report
    </Button>
  );
}
